import { Injectable, Injector } from "@angular/core";
import { DataService } from "./data.service";
import { HttpWrapperService } from "./http.wrapper.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";
import { Router } from "@angular/router";
import { observableToPromise } from "../admin/events-screen/utils/observableToPromise";
import {
  FamilyMemberModel,
  FamilyMemberWithSiblingsModel,
  FamilyModel,
} from "../admin/brothers-attribution/brother-attribution-models/family-model";
import { ServerNamespace } from "../_models/server/server-namespace";
import { Observable } from "rxjs/Observable";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { SaveActionResult } from "../admin/events-screen/event-page-models/save-action-result";

@Injectable()
export class BrotherAttributionService {
  private allSiblingRequests$: BehaviorSubject<FamilyModel[]> =
    new BehaviorSubject<FamilyModel[]>(null);
  constructor(
    dataService: DataService,
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService,
    private injector: Injector,
    private router: Router
  ) {}

  public static serializeFamilyMemberWithSiblingsToFamilyModel(
    memberWithSiblings: FamilyMemberWithSiblingsModel
  ): FamilyModel {
    const siblings = memberWithSiblings.siblings;
    const memberWithoutSiblings = { ...memberWithSiblings };
    delete memberWithoutSiblings.siblings;
    return {
      members: [memberWithoutSiblings, ...siblings],
    };
  }
  public static serializeServerChildRequestRequestsToFamilyMember(
    serverData: ServerNamespace.ChildRequest
  ): FamilyMemberWithSiblingsModel[] {
    return serverData.results.map((childRequest) => {
      const familyMember: FamilyMemberWithSiblingsModel = {
        id: childRequest.id,
        firstName: childRequest.first_name,
        lastName: childRequest.last_name,
        israeliId: childRequest.personal_id,
        address: childRequest.parents.length
          ? childRequest.parents[0].address
          : "-",
        nest: childRequest.ken ? childRequest.ken.name : "-",
        layer: childRequest.grade ? childRequest.grade.name : "-",
        parents: childRequest.parents
          .map((p) => p.first_name + " " + p.last_name)
          .join(", "),
        siblings: childRequest.siblings.map((sibling) => {
          return {
            id: sibling.id,
            firstName: sibling.first_name,
            lastName: sibling.last_name,
            israeliId: "-",
            address: "-",
            nest: sibling.ken ? sibling.ken.name : "-",
            layer: sibling.grade ? sibling.grade.name : "-",
            parents: "-",
          };
        }),
      };
      return familyMember;
    });
  }
  public static serializeServerSiblingRequestsToFamilyModel(
    serverData: ServerNamespace.SiblingRequests
  ): FamilyModel[] {
    return serverData.results.map((siblingRequest) => {
      const family: FamilyModel = {
        requestId: siblingRequest.id,
        members: siblingRequest.siblings.map((sibling) => {
          const address =
            sibling.address.address ||
            "" + sibling.address.house ||
            "" + sibling.address.city ||
            "";
          return {
            id: sibling.id,
            firstName: sibling.first_name,
            lastName: sibling.last_name,
            israeliId: sibling.personal_id,
            address,
            nest: sibling.ken.name,
            layer: sibling.grade.name,
            parents: "-",
          };
        }),
      };
      return family;
    });
  }

  public static serializeFamilyModel(data: {
    childrenAndParents: Array<{
      id: number;
      firstName: string;
      lastName: string;
      isParent: number;
      personalId: string;
      layerName: string;
      nestName: string;
      parentEmail: null;
      parentPhone: null;
      parents: [
        {
          lastName: string;
          firstName: string;
        }
      ];
    }>;
  }): FamilyMemberModel[] {
    if (!data.childrenAndParents) {
      return [];
    }
    return data.childrenAndParents
      .filter((cOrp) => cOrp.isParent === 0)
      .map((childRaw) => {
        const child: FamilyMemberModel = {
          id: childRaw.id,
          firstName: childRaw.firstName,
          lastName: childRaw.lastName,
          israeliId: childRaw.personalId,
          address: "",
          nest: childRaw.nestName,
          layer: childRaw.layerName,
          parents: childRaw.parents
            .map((p) => p.firstName + " " + p.lastName)
            .join(", "),
        };
        return child;
      });
  }
  public async saveFamilyChanges(
    childrenStatuses: Array<{
      id: string;
      status: "new" | "exiting" | "deleted";
    }>
  ): Promise<SaveActionResult> {
    try {
      const body = { siblings: [], to_remove: [] };
      childrenStatuses.forEach((child) => {
        if (child.status === "new" || child.status === "exiting") {
          body.siblings.push(child.id);
        } else if (child.status === "deleted") {
          body.to_remove.push(child.id);
        }
      });
      const response = await observableToPromise(
        this.http.post(`/django-api/sibling-requests/edit/`, body)
      );
      if (response.status === 200) {
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }

  public async searchChildAndSiblingsByText(
    searchText: string,
    maxResults: number = 10
  ): Promise<FamilyMemberWithSiblingsModel[]> {
    if (!searchText || searchText.length < 4) {
      return [];
    }
    const response = await observableToPromise(
      this.http.get(`/django-api/hanich/`, {
        params: { search: searchText, siblings: true },
      })
    );
    const data =
      BrotherAttributionService.serializeServerChildRequestRequestsToFamilyMember(
        await response.json()
      );
    return data.slice(0, maxResults);
  }

  public async getSiblingRequests() {
    const siblingRequestsResponse = await observableToPromise(
      this.http.get("/django-api/sibling-requests/", {
        params: { approved: 0 },
      })
    );
    const siblingRequests =
      (await siblingRequestsResponse.json()) as ServerNamespace.SiblingRequests;
    this.allSiblingRequests$.next(
      BrotherAttributionService.serializeServerSiblingRequestsToFamilyModel(
        siblingRequests
      )
    );
  }

  public getAllSiblingRequests(): Observable<any> {
    return this.allSiblingRequests$.asObservable();
  }

  public async handleSiblingRequest(
    requestId: number,
    action: "approve" | "disapprove"
  ): Promise<SaveActionResult> {
    try {
      const response = await observableToPromise(
        this.http.post(`/django-api/sibling-requests/${requestId}/${action}/`, {
          action,
        })
      );
      if (response.status === 200) {
        return {
          success: true,
          data: response.json(),
        };
      }
      return {
        success: false,
        error: response.json(),
      };
    } catch (e) {
      return {
        success: false,
        error: e,
      };
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/brother-attribution.service.ts